import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Link,
  Grid,
  Avatar,
  useTheme,
  alpha,
  Stack,
} from "@mui/material";
import {
  Business as BusinessIcon,
  LockReset as LockResetIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import WSTextField, { TextType } from "../Components/TextBox/WSTextBox";
import backImg from "../images/login-bg.svg";
import { useForm } from "react-hook-form";
import { callAPI, customValidation, detectPlatform, getDeviceName, LoacalVariables, Method, setValue, toTitleCase, validations } from "../commonFunctions/CommonFunctions";
import { useSnackbar } from "../Components/Snackbar/SnackBarContext";
import { useNavigate } from "react-router-dom";
import ForgotPasswordModal from "./ForgotPasswordModal";

interface LoginFormProps {
  onLogin: (accessToken: string, refreshToken?: string) => void;
}

const Login: React.FC<LoginFormProps> = ({ onLogin }) => {
  const theme = useTheme();

  const [loading, setLoading] = React.useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = React.useState(false);

  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const request = {
        email: data.email,
        password: data.password,
        platform: detectPlatform(),
        deviceName: getDeviceName(),
        userAgent: navigator.userAgent
      };

      const response = await callAPI(
        "Vyapar/User/login",
        Method.POST,
        request
      );
      console.log('Login response:', response); // Debug log to check the API response

      if (response) {
        onLogin(response?.data.accessToken, response?.data.refreshToken);

        //normal;
        setValue(LoacalVariables.UserId, response?.data?.user.id)
        setValue(LoacalVariables.UserType, response?.data?.user.role.toLowerCase())
        setValue(LoacalVariables.Name, toTitleCase(response?.data?.user.userName));
        setValue(LoacalVariables.Email, response?.data?.user.email.toLowerCase());

        //business;
        setValue(LoacalVariables.BusinessName, response?.data?.user.businessName);
        setValue(LoacalVariables.BusinessAddress, response?.data?.user.businessAddress)
        setValue(LoacalVariables.BusinessPhoneNumber, response?.data?.user.businessPhoneNumber)

        const toLowerCaseKeys = (obj: any) => {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
              key.toLowerCase(),
              value
            ])
          );
        };
        setValue(LoacalVariables.Features, toLowerCaseKeys(response?.data?.user.planFeatures));

        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );

        if (response.success === true) {
          navigate("/");
          // window.location.href = "/";
        }
      }
    } catch (error: any) {
      console.error("Failed to login user:", error);
      showMessage(error.response?.data?.message || "Failed to login user", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleForgotPasswordOpen = () => {
    setForgotPasswordOpen(true);
  };

  const handleForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
  };

  const handleForgotPasswordSubmit = async (email: string) => {
    console.log("Password reset requested for:", email);
    try {
      setLoading(true);

      const request = {
        email: email,
      };

      const response = await callAPI(
        "Vyapar/User/forgot-password",
        Method.POST,
        request
      );
      console.log('Forgot password response:', response); // Debug log to check the API response

      if (response) {
        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );

        if (response.success === true) {
          navigate("/");
        }
      }
    } catch (error: any) {
      console.error("Failed to reset password:", error);
      showMessage(error.response?.data?.message || "Failed to reset password", "error");
    } finally {
      setLoading(false);
      handleForgotPasswordClose();
    }
  };
  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url(${backImg})`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            width: "200%",
            height: "200%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, transparent 50%)`,
            animation: "pulse 15s ease-in-out infinite",
          },
          "@keyframes pulse": {
            "0%": {
              transform: "translate(-50%, -50%) rotate(0deg)",
            },
            "100%": {
              transform: "translate(-50%, -50%) rotate(360deg)",
            },
          },
        }}
      >
        <Card
          elevation={24}
          sx={{
            width: 450,
            borderRadius: 4,
            position: "relative",
            overflow: "visible",
            backdropFilter: "blur(10px)",
            background: alpha(theme.palette.background.paper, 0.95),
            boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -30,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            <Avatar
              sx={{
                width: 70,
                height: 70,
                bgcolor: theme.palette.primary.main,
                boxShadow: `0 10px 20px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
              }}
            >
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Avatar>
          </Box>

          <CardContent sx={{ p: 5, pt: 6 }}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: 1,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                letterSpacing: "-0.5px",
              }}
            >
              Welcome Back
            </Typography>

            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Sign in to manage your business
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <WSTextField
                    className="form-control"
                    id="email"
                    type={TextType.Text}
                    rules={customValidation(false, validations.email)}
                    {...register("email")}
                    label="Email Address"
                    control={control}
                    textIcon="Email"

                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.common.white, 0.1),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.common.white, 0.15),
                        },
                        "&.Mui-focused": {
                          backgroundColor: alpha(theme.palette.common.white, 0.2),
                        },
                      },
                    }}

                  />
                </Grid>

                <Grid item xs={12}>
                  <WSTextField
                    label="Password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters",
                      },
                    })}
                    id="password"
                    name="password"
                    control={control}
                    textIcon="Password"
                    type={TextType.Password}

                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.common.white, 0.1),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.common.white, 0.15),
                        },
                        "&.Mui-focused": {
                          backgroundColor: alpha(theme.palette.common.white, 0.2),
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Forgot Password Link */}
              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{ mt: 1, mb: 2 }}
              >
                <Link
                  component="button"
                  type="button"
                  onClick={handleForgotPasswordOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  <LockResetIcon sx={{ fontSize: 16 }} />
                  Forgot Password?
                </Link>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 3,
                  mb: 2,
                }}
              >
                By clicking on the Login button I accept the{" "}
                <Link
                  href="#"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  terms and conditions
                </Link>
              </Typography>

              <Button
                fullWidth
                variant="contained"
                color="error"
                type="submit"
                disabled={!isValid || loading}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  fontSize: "1rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.light} 90%)`,
                  boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.primary.main} 90%)`,
                    boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    background: alpha(theme.palette.primary.main, 0.5),
                    boxShadow: "none",
                  },
                }}
              >
                Login
              </Button>

              {/* Help Section */}
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                sx={{ mt: 3 }}
              >
                {/* <HelpIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} /> */}
                <Typography variant="caption" color="text.secondary">
                  Need help? Contact{" "}
                  <Link
                    href="mailto:info@willsoar.in"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                    }}
                  >
                    info@willsoar.in
                  </Link>
                </Typography>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        onSubmit={handleForgotPasswordSubmit}
      />
    </>
  );
};

export default Login;